import { useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getHydraUIAPIQueryOptions, hydraUiApi } from '@/services/api';
import { getFacebookLeadAdsFormsQueryOptions } from '@/services/db/facebook-lead-ads/queries';
import type { CustomFormResult } from '@/services/auth/types';
import { CLIENT_SIDE_RESULT_CAP, COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';
import {
  filterCustomForms,
  filterFacebookLeadAdsForms,
  mapCustomFormToSearchResult,
  mapFacebookLeadAdsFormToSearchResult,
} from './utils/form-results';
import { mergeQueryStates, type SearchSource } from './utils/source-bookkeeping';

type UseFormsSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type FormsSource = SearchSource<CustomFormResult>;

export function useFormsSource({ workspaceId, enabled, query }: UseFormsSourceOptions): FormsSource {
  const queryClient = useQueryClient();
  const [formsQuery, facebookLeadAdsFormsQuery] = useQueries({
    queries: [
      getHydraUIAPIQueryOptions({
        queryKey: ['getCustomForms', { params: { environment_id: workspaceId } }],
        queryFn: ({ signal }) =>
          hydraUiApi.customForms.list({
            params: { path: { environment_id: String(workspaceId) } },
            signal,
          }),
        queryConfig: {
          enabled,
          ...COMMAND_DIALOG_QUERY_DEFAULTS,
          select: (data) => data.custom_forms,
        },
      }),
      {
        ...getFacebookLeadAdsFormsQueryOptions(workspaceId, queryClient),
        enabled,
        ...COMMAND_DIALOG_QUERY_DEFAULTS,
      },
    ],
  });

  const sourceState = mergeQueryStates([formsQuery, facebookLeadAdsFormsQuery]);
  const results = useMemo(
    () =>
      [
        ...filterCustomForms(formsQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((form) =>
          mapCustomFormToSearchResult(form, workspaceId),
        ),
        ...filterFacebookLeadAdsForms(facebookLeadAdsFormsQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((form) =>
          mapFacebookLeadAdsFormToSearchResult(form, workspaceId),
        ),
      ].slice(0, CLIENT_SIDE_RESULT_CAP),
    [formsQuery.data, facebookLeadAdsFormsQuery.data, query, workspaceId],
  );

  return { ...sourceState, results };
}
